import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { API_BASE } from "@/lib/api";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";

interface ReleaseGame {
  appid: string;
  name: string;
  capsule_image: string;
  release_date: string;
  price_text: string;
  platforms: { windows: boolean; mac: boolean; linux: boolean };
  review_summary: string;
  source: "upcoming" | "recent";
}

type ReleaseFilter = "all" | "upcoming" | "recent";

function parseReleaseDate(dateStr: string): Date | null {
  const krMatch = dateStr.match(/(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일/);
  if (krMatch) return new Date(parseInt(krMatch[1]), parseInt(krMatch[2]) - 1, parseInt(krMatch[3]));
  const enMatch = dateStr.match(/(\w+)\s+(\d{1,2}),?\s+(\d{4})/);
  if (enMatch) { const d = new Date(`${enMatch[1]} ${enMatch[2]}, ${enMatch[3]}`); if (!isNaN(d.getTime())) return d; }
  const enMatch2 = dateStr.match(/(\d{1,2})\s+(\w+),?\s+(\d{4})/);
  if (enMatch2) { const d = new Date(`${enMatch2[2]} ${enMatch2[1]}, ${enMatch2[3]}`); if (!isNaN(d.getTime())) return d; }
  return null;
}

function getMonday(d: Date): Date {
  const date = new Date(d); const day = date.getDay(); date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day)); date.setHours(0, 0, 0, 0); return date;
}
function addDays(d: Date, days: number): Date { const date = new Date(d); date.setDate(date.getDate() + days); return date; }
function isSameDay(a: Date, b: Date): boolean { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function dateKey(d: Date): string { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

const DAY_NAMES_KO = ["일", "월", "화", "수", "목", "금", "토"];

export default function ReleasesPage() {
  const { t } = useLang();
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [weekStart, setWeekStart] = useState(() => getMonday(today));
  const [games, setGames] = useState<ReleaseGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ReleaseFilter>("all");
  const todayLineRef = useRef<HTMLDivElement>(null);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const formatDate = (d: Date) => `${d.getMonth() + 1}월 ${d.getDate()}일`;
  const formatDateFull = (d: Date) => `${d.getMonth() + 1}월 ${d.getDate()}일 (${DAY_NAMES_KO[d.getDay()]})`;

  const fetchGames = useCallback(async () => {
    setLoading(true);
    try {
      const [upRes, recRes] = await Promise.allSettled([
        fetch(`${API_BASE}/store/upcoming?type=upcoming&count=100&lang=ko`),
        fetch(`${API_BASE}/store/upcoming?type=recent&count=100&lang=ko`),
      ]);
      const all: ReleaseGame[] = [];
      if (upRes.status === "fulfilled") { const d = await upRes.value.json(); all.push(...(d.results || []).map((g: ReleaseGame) => ({ ...g, source: "upcoming" as const }))); }
      if (recRes.status === "fulfilled") { const d = await recRes.value.json(); all.push(...(d.results || []).map((g: ReleaseGame) => ({ ...g, source: "recent" as const }))); }
      setGames(all);
    } catch { setGames([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGames(); }, [fetchGames]);

  const filteredGames = useMemo(() => filter === "all" ? games : games.filter((g) => g.source === filter), [games, filter]);

  const weekDays = useMemo(() => {
    const gameMap: Record<string, ReleaseGame[]> = {};
    for (const game of filteredGames) {
      const d = parseReleaseDate(game.release_date);
      if (d) { const k = dateKey(d); if (!gameMap[k]) gameMap[k] = []; gameMap[k].push(game); }
    }
    const days: { date: Date; key: string; games: ReleaseGame[]; isToday: boolean; isPast: boolean }[] = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(weekStart, i);
      const key = dateKey(date);
      days.push({ date, key, games: gameMap[key] || [], isToday: isSameDay(date, today), isPast: date < today });
    }
    return days;
  }, [weekStart, filteredGames, today]);

  const weekTotal = weekDays.reduce((sum, d) => sum + d.games.length, 0);
  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const goThisWeek = () => setWeekStart(getMonday(today));
  const isThisWeek = isSameDay(weekStart, getMonday(today));

  useEffect(() => {
    if (!loading && todayLineRef.current) todayLineRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [loading, weekStart]);

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-6 flex-1 w-full">
      {/* Header */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
          </div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{t.releases?.title || "릴리즈 캘린더"}</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">{t.calendar?.description || "Steam에서 최근 출시된 게임과 출시 예정인 게임들을 캘린더로 확인할 수 있어요."}</p>
      </div>

      {/* Navigation + Filter */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prevWeek} className="w-9 h-9 flex items-center justify-center rounded-lg bg-card-bg border border-gray-200 dark:border-card-border hover:bg-hover-bg transition-colors text-gray-600 dark:text-muted">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <h2 className="text-sm font-bold text-gray-900 dark:text-white tabular-nums min-w-[160px] text-center">{formatDate(weekStart)} ~ {formatDate(weekEnd)}</h2>
          <button onClick={nextWeek} className="w-9 h-9 flex items-center justify-center rounded-lg bg-card-bg border border-gray-200 dark:border-card-border hover:bg-hover-bg transition-colors text-gray-600 dark:text-muted">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
          </button>
          {!isThisWeek && <button onClick={goThisWeek} className="ml-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors">{t.releases?.thisWeek || "이번 주"}</button>}
        </div>
        <div className="flex items-center gap-1 bg-card-bg border border-gray-200 dark:border-card-border rounded-xl p-0.5">
          {([{ key: "all" as ReleaseFilter, label: t.common?.all || "전체" }, { key: "recent" as ReleaseFilter, label: t.common?.released || "출시됨" }, { key: "upcoming" as ReleaseFilter, label: t.releases?.upcomingLabel || "출시 예정" }]).map((tab) => (
            <button key={tab.key} onClick={() => setFilter(tab.key)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === tab.key ? "bg-gray-900 text-white shadow-sm" : "text-gray-500 dark:text-muted hover:text-gray-900 dark:hover:text-white"}`}>{tab.label}</button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 px-1 flex-wrap">
        <p className="text-xs text-gray-500 dark:text-muted">이번 주: <span className="font-bold text-gray-900 dark:text-white">{weekTotal}</span>개 게임</p>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-[10px] text-gray-400 dark:text-sub-text">출시됨</span></div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500" /><span className="text-[10px] text-gray-400 dark:text-sub-text">출시 예정</span></div>
      </div>

      {/* Weekly timeline */}
      {loading ? <Spinner /> : (
        <div className="pb-8">
          {weekDays.map((day) => (
            <div key={day.key}>
              {/* Today red divider */}
              {day.isToday && (
                <div ref={todayLineRef} className="flex items-center gap-2 mt-4 mb-2">
                  <div className="flex-1 h-[2px] bg-red-500" />
                  <span className="text-xs font-bold text-red-500 shrink-0">{t.calendar?.today || "오늘"}</span>
                  <div className="flex-1 h-[2px] bg-red-500" />
                </div>
              )}
              {/* Day header */}
              <div ref={!day.isToday && day.games.length > 0 ? undefined : undefined} className={`flex items-center gap-3 ${day.isToday ? "mt-0" : "mt-3"} mb-1.5 ${day.isPast && !day.isToday ? "opacity-50" : ""}`}>
                <h3 className={`text-sm font-bold tabular-nums ${day.isToday ? "text-red-500" : "text-gray-400 dark:text-sub-text"}`}>
                  {formatDateFull(day.date)}
                </h3>
                {day.games.length > 0 && <span className="text-xs text-gray-400 dark:text-sub-text">{day.games.length}개</span>}
                <div className="flex-1 h-px bg-gray-200 dark:bg-card-border" />
              </div>

              {/* Games */}
              {day.games.length === 0 ? (
                <div className={`mb-1 py-1.5 text-center text-[11px] text-gray-400 dark:text-sub-text ${day.isPast ? "opacity-50" : ""}`}>—</div>
              ) : (
                <div className={`mb-1 ${day.isPast && !day.isToday ? "opacity-60" : ""}`}>
                  <div className="bg-card-bg dark:bg-card-bg rounded-lg border border-gray-200 dark:border-card-border overflow-hidden">
                    {day.games.map((game, idx) => (
                      <Link key={game.appid} to={`/game/${game.appid}`} className={`group flex items-center gap-2 px-2.5 py-1.5 hover:bg-hover-bg dark:hover:bg-hover-bg transition-colors ${idx > 0 ? "border-t border-gray-100 dark:border-card-border/60" : ""}`}>
                        <div className="relative shrink-0 w-[60px] h-[28px] rounded overflow-hidden bg-hover-bg dark:bg-background hidden sm:block">
                          <img src={game.capsule_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`} alt={game.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <p className="flex-1 min-w-0 text-xs sm:text-sm text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 font-medium truncate transition-colors">{game.name}</p>
                        <span className={`shrink-0 text-[9px] font-bold px-1 py-0.5 rounded ${game.source === "recent" ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400"}`}>
                          {game.source === "recent" ? "출시" : "예정"}
                        </span>
                        <span className="shrink-0 text-[11px] text-gray-600 dark:text-sub-text font-medium tabular-nums min-w-[55px] text-right">{game.price_text || ""}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <AdBanner className="my-4" />
    </div>
  );
}
